import { useState, type JSX } from "react";

import { useGroups, useMembers } from "../people/peopleQueries.js";
import type { CreateShareRequest, MemberDTO, MembershipRole, ShareDTO, ShareLevel } from "../api/types.js";
import { PrimaryButton } from "./PrimaryButton.js";
import { useModalFocus } from "./useModalFocus.js";

const LEVEL_LABELS: Record<ShareLevel, string> = {
  VIEW: "Ansehen",
  COMMENT: "Kommentieren",
  EDIT: "Bearbeiten",
  MANAGE: "Verwalten",
};

/**
 * Freigabe einer Ressource — dieselbe Oberfläche für Dateien und Kalender, weil
 * dahinter dieselbe Freigabe-Tabelle steht.
 */
export function ShareDialog({
  title,
  shares,
  canManage,
  levels = ["VIEW", "EDIT", "MANAGE"],
  allowPublic = true,
  onShare,
  onRevoke,
  onClose,
  busy,
  error,
}: {
  title: string;
  shares: ShareDTO[];
  canManage: boolean;
  levels?: ShareLevel[];
  allowPublic?: boolean;
  onShare: (input: CreateShareRequest) => void;
  onRevoke: (shareId: string) => void;
  onClose: () => void;
  busy: boolean;
  error: string | null;
}): JSX.Element {
  const [search, setSearch] = useState("");
  const [level, setLevel] = useState<ShareLevel>("VIEW");
  const panel = useModalFocus(onClose);
  const members = useMembers(search, canManage);
  const groups = useGroups(canManage);

  const alreadyShared = new Set(shares.map((share) => share.userId));
  const candidates = (members.data ?? []).filter((member) => !alreadyShared.has(member.userId));

  return (
    <div className="fixed inset-0 z-30 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-6">
      {/* Am Telefon dockt der Dialog unten an — dort sitzt die Gestenleiste. */}
      <div ref={panel} role="dialog" aria-modal="true" tabIndex={-1} className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-t-2xl border border-line bg-surface-raised p-5 pb-[calc(theme(spacing.5)+env(safe-area-inset-bottom))] shadow-2xl sm:rounded-2xl sm:pb-5">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold text-ink">Freigeben</h2>
            <p className="mt-0.5 truncate text-sm text-ink-muted">{title}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-sm text-ink-muted hover:bg-surface-sunken hover:text-ink"
          >
            Schließen
          </button>
        </div>

        {error ? <p className="mb-3 text-sm text-danger">{error}</p> : null}

        <section className="mb-5">
          <h3 className="mb-2 text-sm font-medium text-ink">Bereits freigegeben</h3>
          {shares.length === 0 ? (
            <p className="text-sm text-ink-muted">Noch niemand. Nur du hast Zugriff.</p>
          ) : (
            <ul className="space-y-1">
              {shares.map((share) => (
                <li
                  key={share.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-line px-3 py-2"
                >
                  <span className="min-w-0 truncate text-sm text-ink">
                    {shareLabel(share)}
                  </span>
                  <span className="shrink-0 text-xs text-ink-muted">{LEVEL_LABELS[share.level]}</span>
                  {canManage ? (
                    <button
                      type="button"
                      onClick={() => onRevoke(share.id)}
                      disabled={busy}
                      className="shrink-0 rounded px-2 py-1 text-xs text-danger hover:bg-surface-sunken disabled:opacity-50"
                    >
                      Entfernen
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </section>

        {canManage ? (
          <section>
            <h3 className="mb-2 text-sm font-medium text-ink">Hinzufügen</h3>
            <div className="mb-3 flex gap-2">
              <input
                value={search}
                onChange={(changed) => setSearch(changed.target.value)}
                placeholder="Name oder Mailadresse"
                className="min-w-0 flex-1 rounded-lg border border-line bg-surface px-3 py-2 text-sm text-ink"
              />
              <label className="shrink-0">
                <span className="sr-only">Berechtigung</span>
                <select
                  value={level}
                  onChange={(changed) => setLevel(changed.target.value as ShareLevel)}
                  className="rounded-lg border border-line bg-surface px-2 py-2 text-sm text-ink"
                >
                  {levels.map((entry) => (
                    <option key={entry} value={entry}>
                      {LEVEL_LABELS[entry]}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <PrimaryButton
              type="button"
              className="mb-3 w-full"
              disabled={busy}
              onClick={() => onShare({ subject: "TENANT", level })}
            >
              Für alle im Unternehmen freigeben
            </PrimaryButton>

            {allowPublic ? <button
              type="button"
              disabled={busy}
              onClick={() => onShare({ subject: "PUBLIC", level })}
              className="mb-3 w-full rounded-lg border border-emerald-600/40 px-3 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-500/10 disabled:opacity-50 dark:text-emerald-400"
            >
              Öffentlich freigeben
            </button> : null}

            <div className="mb-3 grid grid-cols-2 gap-2">
              {(["OWNER", "ADMIN", "MEMBER", "GUEST"] as MembershipRole[]).map((role) => (
                <button key={role} type="button" disabled={busy} onClick={() => onShare({ subject: "ROLE", role, level })} className="rounded-lg border border-line px-3 py-2 text-left text-sm text-ink hover:bg-surface-sunken disabled:opacity-50">
                  Rolle: {roleLabel(role)}
                </button>
              ))}
              {(groups.data ?? []).map((group) => (
                <button key={group.id} type="button" disabled={busy} onClick={() => onShare({ subject: "GROUP", groupId: group.id, level })} className="rounded-lg border border-line px-3 py-2 text-left text-sm text-ink hover:bg-surface-sunken disabled:opacity-50">
                  Gruppe: {group.name}
                </button>
              ))}
            </div>

            {members.isPending ? <p className="text-sm text-ink-muted">Wird geladen …</p> : null}
            <ul className="max-h-56 space-y-1 overflow-y-auto">
              {candidates.map((member: MemberDTO) => (
                <li key={member.userId}>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => onShare({ subject: "USER", userId: member.userId, level })}
                    className="flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left hover:bg-surface-sunken disabled:opacity-50"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm text-ink">
                        {member.displayName}
                        {member.kind === "AGENT" ? " · Agent" : ""}
                      </span>
                      <span className="block truncate text-xs text-ink-muted">{member.email}</span>
                    </span>
                    <span className="shrink-0 text-xs text-brand">Freigeben</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : (
          <p className="text-sm text-ink-muted">Nur wer verwalten darf, kann weiter freigeben.</p>
        )}
      </div>
    </div>
  );
}

function roleLabel(role: MembershipRole): string {
  return { OWNER: "Eigentümer", ADMIN: "Administratoren", MEMBER: "Mitglieder", GUEST: "Gäste" }[role];
}

function shareLabel(share: ShareDTO): string {
  if (share.subject === "PUBLIC") return "Öffentlich";
  if (share.subject === "TENANT") return "Alle im Unternehmen";
  if (share.subject === "ROLE" && share.role) return `Rolle: ${roleLabel(share.role)}`;
  if (share.subject === "GROUP") return `Gruppe: ${share.groupName ?? "Unbekannt"}`;
  return share.userDisplayName ?? share.userEmail ?? "Unbekannt";
}
